import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { EVAULT_SIMULATOR_ADDRESS } from '@/config/consts'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { PageState, PageStatus } from '@/features/dialogs/common/types'
import { useQueryClient } from '@tanstack/react-query'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { evaultSimulatorABI } from 'abis/evm/eVaultSimulator'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Address, formatUnits, parseUnits } from 'viem'
import {
  useAccount,
  useConfig,
  useReadContract,
  useReadContracts,
  useWriteContract,
} from 'wagmi'
import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import {
  type TransactionItems,
  type EnableCollateralTransactionItem,
  type DisableCollateralTransactionItem,
  TransactionLabel,
  useSimulateEVCBatchCall,
} from '@eulerxyz/wagmi-evc'

interface UseECollateralStatusDialogParams {
  collateralVault: Address
  collateralSymbol: string
  collateralDecimals: number
  collateralAsset: Address
  collateralLogoURI?: string
  currentCollateralFormatted: number
  collateralPriceUSD: number
  subAccountId: number
  evcAddress: Address
  action: 'enable' | 'disable'
  /** Current debt in USD for health factor calculation */
  currentDebtUSD: number
  /** Total collateral currently backing debt (USD) */
  accountTotalCollateralUSD: number
  /** Liquidation LTV threshold (percentage, e.g., 85 for 85%) */
  liquidationLTV: number
}

export interface UseECollateralStatusDialogResult {
  collateralToken: Token | undefined
  pageStatus: PageStatus
  isSimulating: boolean
  simulationError: Error | null
  simulationSuccess: boolean
  error: Error | null
  errorMessage: string | null
  canExecute: boolean
  isLoading: boolean
  handleConfirm: () => void
  collateralValueUSD: number
  currentHealthFactor: number
  newHealthFactor: number
  newLTV: number
  isSimulatingHealthFactor: boolean
}

export function useECollateralStatusDialog({
  collateralVault,
  collateralSymbol,
  collateralDecimals,
  collateralAsset,
  collateralLogoURI,
  currentCollateralFormatted,
  collateralPriceUSD,
  subAccountId,
  evcAddress,
  action,
  currentDebtUSD,
  accountTotalCollateralUSD,
  liquidationLTV,
}: UseECollateralStatusDialogParams): UseECollateralStatusDialogResult {
  const [pageState, setPageState] = useState<PageState>('form')
  const { address: userAddress } = useAccount()
  const queryClient = useQueryClient()

  // Calculate sub-account address
  const subAccountAddress = useMemo(() => {
    if (!userAddress) return undefined
    return getSubAccountAddress(userAddress, subAccountId)
  }, [userAddress, subAccountId])

  // Get controllers (liability vaults) from EVC
  const { data: controllers } = useReadContract({
    address: evcAddress,
    abi: eulerVaultConnectorAbi,
    functionName: 'getControllers',
    args: subAccountAddress ? [subAccountAddress] : undefined,
    chainId: E_LEND_CHAIN_ID,
    query: {
      enabled: !!evcAddress && !!subAccountAddress,
    },
  })

  // For 'enable' action: fetch borrowLTV for the collateral from the first liability vault
  // This tells us how much this collateral contributes to the risk-adjusted value
  const firstController = controllers?.[0] as Address | undefined
  const { data: ltvConfigData, isLoading: isLoadingLtvConfig } = useReadContract({
    address: firstController,
    abi: eVaultImplementationAbi,
    functionName: 'LTVFull',
    args: [collateralVault],
    chainId: E_LEND_CHAIN_ID,
    query: {
      enabled: !!firstController && action === 'enable',
    },
  })

  // Parse borrowLTV and liquidationLTV from LTVFull result (basis points: 10000 = 100%)
  // LTVFull returns: borrowLTV (uint16), liquidationLTV (uint16), initialLiquidationLTV (uint16), targetTimestamp (uint48), rampDuration (uint32)
  const { collateralBorrowLTV, collateralLiquidationLTV } = useMemo(() => {
    if (!ltvConfigData || action !== 'enable')
      return { collateralBorrowLTV: undefined, collateralLiquidationLTV: undefined }
    const [borrowLTV, liqLTV] = ltvConfigData as readonly [number, number, number, number, number]
    return {
      collateralBorrowLTV: borrowLTV > 0 ? borrowLTV / 10000 : 0,
      collateralLiquidationLTV: liqLTV > 0 ? liqLTV / 10000 : 0,
    }
  }, [ltvConfigData, action])

  // Calculate collateral amount in raw units for simulation
  const collateralAmountRaw = useMemo(() => {
    try {
      return parseUnits(currentCollateralFormatted.toString(), collateralDecimals)
    } catch {
      return 0n
    }
  }, [currentCollateralFormatted, collateralDecimals])

  // Build simulation contracts for health factor
  // For disable collateral: simulate as if withdrawing the full amount
  // For enable collateral: we'll estimate locally (enabling always improves HF)
  const healthSimulationContracts = useMemo(() => {
    if (!controllers || controllers.length === 0 || !subAccountAddress) return []
    if (action !== 'disable') return [] // Only simulate for disable
    if (collateralAmountRaw === 0n) return []

    return controllers.map((liabilityVault: Address) => ({
      address: EVAULT_SIMULATOR_ADDRESS,
      abi: evaultSimulatorABI,
      functionName: 'simulateHealthFactorAfterWithdraw' as const,
      args: [liabilityVault, subAccountAddress, collateralVault, collateralAmountRaw] as const,
      chainId: E_LEND_CHAIN_ID,
    }))
  }, [controllers, subAccountAddress, collateralVault, collateralAmountRaw, action])

  // Simulate health factor after disable
  const { data: healthSimulationResults, isLoading: isSimulatingHealthFactor } = useReadContracts({
    contracts: healthSimulationContracts,
    query: {
      enabled: healthSimulationContracts.length > 0,
    },
  })

  // Get current health factor from simulator for each liability vault
  const currentHealthContracts = useMemo(() => {
    if (!controllers || controllers.length === 0 || !subAccountAddress) return []

    return controllers.map((liabilityVault: Address) => ({
      address: EVAULT_SIMULATOR_ADDRESS,
      abi: evaultSimulatorABI,
      functionName: 'getHealthFactor' as const,
      args: [liabilityVault, subAccountAddress, true] as const, // true for liquidation threshold
      chainId: E_LEND_CHAIN_ID,
    }))
  }, [controllers, subAccountAddress])

  const { data: currentHealthResults, isLoading: isLoadingCurrentHealth } = useReadContracts({
    contracts: currentHealthContracts,
    query: {
      enabled: currentHealthContracts.length > 0,
    },
  })

  // Parse current health factor (minimum across all liabilities)
  const currentHealthFactor = useMemo(() => {
    if (!currentHealthResults || currentHealthResults.length === 0) {
      // Fallback to local calculation
      const currentLTV =
        accountTotalCollateralUSD > 0
          ? (currentDebtUSD / accountTotalCollateralUSD) * 100
          : currentDebtUSD > 0
            ? Number.POSITIVE_INFINITY
            : 0
      return currentLTV > 0 ? liquidationLTV / currentLTV : Number.POSITIVE_INFINITY
    }

    const MAX_UINT256 = 2n ** 256n - 1n
    let minHealthFactor: number | undefined = undefined

    for (const result of currentHealthResults) {
      if (result.status === 'success' && result.result !== undefined) {
        const rawValue = result.result as bigint
        // Skip if no debt (max uint256)
        if (rawValue >= MAX_UINT256 - 1n) continue
        const healthFactor = Number(formatUnits(rawValue, 18))
        if (minHealthFactor === undefined || healthFactor < minHealthFactor) {
          minHealthFactor = healthFactor
        }
      }
    }

    return minHealthFactor ?? Number.POSITIVE_INFINITY
  }, [currentHealthResults, accountTotalCollateralUSD, currentDebtUSD, liquidationLTV])

  // Parse new health factor after action
  const newHealthFactor = useMemo(() => {
    if (action === 'enable') {
      // For enable: calculate based on the collateral's borrowLTV and liquidationLTV
      if (collateralBorrowLTV === undefined || collateralBorrowLTV === 0) {
        // If borrowLTV is 0 or not loaded, collateral won't contribute - HF stays same
        return currentHealthFactor
      }

      if (currentDebtUSD <= 0) {
        return Number.POSITIVE_INFINITY // No debt
      }

      // Calculate risk-adjusted contribution of this collateral
      const collateralValueUSD = currentCollateralFormatted * collateralPriceUSD
      const riskAdjustedContribution = collateralValueUSD * collateralBorrowLTV
      const liqAdjustedContribution = collateralValueUSD * (collateralLiquidationLTV || liquidationLTV / 100)

      // Derive current risk-adjusted collateral from current HF
      // HF = liquidationLTV * riskAdjustedCollateral / (debt * 100)
      // So: riskAdjustedCollateral = HF * debt * 100 / liquidationLTV
      const currentRiskAdjustedCollateralUSD = (currentHealthFactor * currentDebtUSD * 100) / liquidationLTV

      // Current liquidation-adjusted collateral
      const currentLiqAdjustedCollateralUSD = currentRiskAdjustedCollateralUSD * (liquidationLTV / 100)

      // Add new collateral's contribution (both borrow and liquidation adjusted)
      const newRiskAdjustedCollateralUSD = currentRiskAdjustedCollateralUSD + riskAdjustedContribution
      const newLiqAdjustedCollateralUSD = currentLiqAdjustedCollateralUSD + liqAdjustedContribution

      // Calculate new effective liquidation LTV (weighted by liquidation-adjusted values)
      const newEffectiveLiqLTV =
        newRiskAdjustedCollateralUSD > 0
          ? (newLiqAdjustedCollateralUSD / newRiskAdjustedCollateralUSD) * 100
          : liquidationLTV

      // Calculate new HF with the new effective liquidation LTV
      return (newEffectiveLiqLTV * newRiskAdjustedCollateralUSD) / (currentDebtUSD * 100)
    }

    // For disable: use on-chain simulation result
    if (!healthSimulationResults || healthSimulationResults.length === 0) {
      // Fallback to local calculation
      const collateralValueUSD = currentCollateralFormatted * collateralPriceUSD
      const newAccountCollateralUSD = Math.max(0, accountTotalCollateralUSD - collateralValueUSD)
      const newLTV =
        newAccountCollateralUSD > 0
          ? (currentDebtUSD / newAccountCollateralUSD) * 100
          : currentDebtUSD > 0
            ? Number.POSITIVE_INFINITY
            : 0
      return newLTV > 0 ? liquidationLTV / newLTV : Number.POSITIVE_INFINITY
    }

    const MAX_UINT256 = 2n ** 256n - 1n
    let minHealthFactor: number | undefined = undefined
    let hasValidResult = false

    for (const result of healthSimulationResults) {
      if (result.status === 'success' && result.result !== undefined) {
        const rawValue = result.result as bigint
        // Skip if no debt (max uint256) - this is a healthy position
        if (rawValue >= MAX_UINT256 - 1n) {
          hasValidResult = true
          continue
        }
        hasValidResult = true
        const healthFactor = Number(formatUnits(rawValue, 18))
        if (minHealthFactor === undefined || healthFactor < minHealthFactor) {
          minHealthFactor = healthFactor
        }
      }
    }

    // If minHealthFactor is undefined but we had valid results, all were max uint256 (no debt) = healthy
    if (minHealthFactor === undefined && hasValidResult) {
      return Number.POSITIVE_INFINITY
    }

    // If we have a calculated health factor, return it
    // If simulation failed entirely (no valid results), return 0 (liquidation risk)
    return minHealthFactor ?? 0
  }, [
    action,
    collateralBorrowLTV,
    collateralLiquidationLTV,
    currentHealthFactor,
    healthSimulationResults,
    currentCollateralFormatted,
    collateralPriceUSD,
    accountTotalCollateralUSD,
    currentDebtUSD,
    liquidationLTV,
  ])

  const config = useConfig()

  // Build wagmi-evc TransactionItems for consistent factory pipeline + validation
  const transactions = useMemo((): TransactionItems => {
    if (!subAccountAddress) return []

    const asset = {
      address: collateralAsset,
      name: collateralSymbol,
      symbol: collateralSymbol,
      decimals: collateralDecimals,
    }

    if (action === 'enable') {
      const tx: EnableCollateralTransactionItem = {
        label: TransactionLabel.ENABLE_COLLATERAL,
        vault: collateralVault,
        asset,
        from: subAccountAddress,
        to: subAccountAddress,
      }
      return [tx]
    }

    const tx: DisableCollateralTransactionItem = {
      label: TransactionLabel.DISABLE_COLLATERAL,
      vault: collateralVault,
      asset,
      from: subAccountAddress,
      to: subAccountAddress,
    }
    return [tx]
  }, [subAccountAddress, collateralVault, collateralAsset, collateralSymbol, collateralDecimals, action])

  // Simulate via useSimulateEVCBatchCall for consistent validation with other dialogs
  const {
    data: simulateData,
    error: simulateError,
    isLoading: isSimulating,
  } = useSimulateEVCBatchCall({
    config,
    transactions,
    query: {
      enabled: transactions.length > 0 && !!subAccountAddress,
    },
  })

  // Write contract
  const { writeContract, data: txHash, isPending, error: writeError, reset: resetWrite } = useWriteContract()
  const { isLoading: isConfirming, isSuccess, error: txError } = useWaitForTransactionReceiptUniversal({ hash: txHash })

  // Handle success
  useEffect(() => {
    if (isSuccess) {
      queryClient.invalidateQueries({ queryKey: ['readContract'] })
      queryClient.invalidateQueries({ queryKey: ['readContracts'] })
      setPageState('success')
    }
  }, [isSuccess, queryClient])

  // Create collateral token
  const collateralToken = useMemo(() => {
    if (!collateralSymbol) return undefined
    return new Token({
      symbol: TokenSymbol(collateralSymbol),
      name: collateralSymbol,
      decimals: collateralDecimals,
      address: CheckedAddress(collateralAsset),
      unitPriceUsd: collateralPriceUSD.toString(),
      logoURI: collateralLogoURI,
    })
  }, [collateralSymbol, collateralDecimals, collateralAsset, collateralLogoURI, collateralPriceUSD])

  const isLoading = isPending || isConfirming
  const canExecute = !isSimulating && !simulateError && !!simulateData
  const simulationSuccess = !isSimulating && !simulateError && !!simulateData

  // Reset write error when user wants to retry
  const handleConfirmWithReset = useCallback(() => {
    if (writeError) {
      resetWrite()
    }
    if (!simulateData?.request) return
    writeContract(simulateData.request as any)
  }, [simulateData, writeContract, writeError, resetWrite])

  // Parse error message (handles simulation, write, and transaction errors)
  const error = (simulateError || writeError || txError) as Error | null

  // Parse error message (handles simulation, write, and transaction errors)
  const errorMessage = useMemo(() => {
    if (!error) return null

    const message = error.message || ''

    // User rejected transaction - no error to display
    if (message.includes('User rejected') || message.includes('user rejected')) {
      return null
    }

    // Simulation/contract errors
    if (message.includes('E_OutstandingDebt')) {
      return 'Cannot disable collateral while you have outstanding debt. Repay your debt first.'
    }
    if (message.includes('E_BadCollateral')) {
      return 'This vault cannot be used as collateral.'
    }
    if (message.includes('E_ControllerDisabled')) {
      return 'Controller is not enabled for this position.'
    }

    // Transaction failed on-chain
    if (txError) {
      return 'Transaction failed. Please try again.'
    }

    // Write error (wallet issues)
    if (writeError) {
      return 'Failed to submit transaction. Please try again.'
    }

    return 'Transaction would fail. Please check your position.'
  }, [error, txError, writeError])

  // Calculate collateral value
  const collateralValueUSD = currentCollateralFormatted * collateralPriceUSD

  // Calculate new LTV from simulated health factor
  // Since healthFactor = liquidationLTV / LTV, we can derive: LTV = liquidationLTV / healthFactor
  // This is more accurate as it uses on-chain simulation that accounts for different collateral LTV weights
  const newLTV = useMemo(() => {
    // If no debt, LTV is 0
    if (currentDebtUSD <= 0) return 0

    // Derive from simulated health factor (which comes from on-chain simulation)
    if (newHealthFactor > 0 && Number.isFinite(newHealthFactor)) {
      return liquidationLTV / newHealthFactor
    }

    // If health factor is 0 or infinite, handle edge cases
    if (newHealthFactor === 0) {
      return Number.POSITIVE_INFINITY // Liquidation
    }

    // newHealthFactor is infinite = no debt or fully collateralized
    return 0
  }, [currentDebtUSD, newHealthFactor, liquidationLTV])

  // Callback for when transaction succeeds
  const goToSuccessScreen = useCallback(() => {
    setPageState('success')
  }, [])

  return {
    collateralToken,
    pageStatus: {
      state: pageState,
      actionsEnabled: canExecute && !isLoading,
      goToSuccessScreen,
    },
    isSimulating,
    simulationError: simulateError as Error | null,
    simulationSuccess,
    error,
    errorMessage,
    canExecute,
    isLoading,
    handleConfirm: handleConfirmWithReset,
    collateralValueUSD,
    currentHealthFactor,
    newHealthFactor,
    newLTV,
    isSimulatingHealthFactor: isSimulatingHealthFactor || isLoadingCurrentHealth || isLoadingLtvConfig,
  }
}
